import { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../Configurations/APIService';
import config from '../Configurations/APIConfig';

const TenantContext = createContext(null);

// Helper to parse JSON safely
const safeJSON = (str, fallback = {}) => {
  if (!str || str === 'string' || str === 'null') return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// Extracts the clean domain (removes www.)
const extractDomain = () => {
  let host = window.location.hostname.toLowerCase();
  if (host.startsWith('www.')) host = host.replace('www.', '');
  return host;
};

export const TenantProvider = ({ children }) => {
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenantData = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlTemplate = params.get('template');
      const domain = extractDomain();

      // ⭐ 1. Sticky Preview Logic
      // If template is in URL, save it to session. If not, try to get it from session.
      if (urlTemplate) {
        sessionStorage.setItem('preview_template', urlTemplate);
      }
      const previewTemplate = urlTemplate || sessionStorage.getItem('preview_template');

      // ⭐ 2. Generic Preview Detection
      // If we have a template override and we are on a test environment, skip DB lookup
      const isGenericPreview = previewTemplate && (domain === 'localhost' || domain.includes('trawtel.com'));

      if (isGenericPreview) {
        console.log("Preview Mode Active: Skipping domain lookup to prevent API errors.");
        setTenantData({
          templateReference: previewTemplate,
          branding: {},
          theme: {},
          header: [],
          hero: {},
          footer: {},
          aboutUs: {},
          contactUs: {},
          seo: {},
          destinations: [],
          activeModules: {},
          layout: {}
        });
        setLoading(false);
        return; // Stop execution here
      }

      // ⭐ 3. Normal Database Fetch Logic (Real Agent Domains)
      try {
        // Fallback for developers who want to load data on localhost without ?template=
        const activeDomain = domain === 'localhost' ? 'myfirstbooking.in' : domain;
        
        const res = await apiService.getAll(`${config.GETTEMPLATEBYDOMAIN}/${activeDomain}`);
        const data = res.data || res;

        // If we found a real agent, clear the preview memory to avoid interference
        sessionStorage.removeItem('preview_template');

        setTenantData({
          // Core Identifiers
          companyId:         data.companyId,
          templateId:        data.templateId,
          domain:            data.domain,

          // Design Reference (URL parameter wins over Database)
          templateReference: previewTemplate || data.templateReference || data.templateRefference || 'Template_1',

          // All parsed sections
          branding:      safeJSON(data.customBrandingJson, {}),
          theme:         safeJSON(data.styleJson, {}),
          header:        safeJSON(data.headerJson, []),
          hero:          safeJSON(data.heroJson, {}),
          footer:        safeJSON(data.footerJson, {}),
          aboutUs:       safeJSON(data.aboutUsJson, {}),
          contactUs:     safeJSON(data.contactUsJson, {}),
          seo:           safeJSON(data.seoJson, {}),
          destinations:  safeJSON(data.destinationsJson, { destinations: [] }).destinations || [],
          activeModules: safeJSON(data.activeModulesJson, {}),
          layout:        safeJSON(data.layoutJson, {}),
        });
      } catch (err) {
        console.error('Domain fetch failed:', err);
        // Last resort fallback
        setTenantData({ templateReference: previewTemplate || 'Template_1' });
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, []);

  return (
    <TenantContext.Provider value={{ tenantData, loading }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside TenantProvider');
  }
  return ctx;
};

export default TenantContext;